/**
 * ⚠️  INTERNAL USE ONLY
 * Repair KB Taxonomy management — /admin/repair-knowledge-base/taxonomy
 */
import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AdminLoginGate, isAdminAuthed, clearAdminAuth } from '../../components/AdminLoginGate';
import {
  fetchTaxonomy,
  createTaxonomyItem,
  updateTaxonomyItem,
  deleteTaxonomyItem,
  invalidateTaxonomyCache,
} from '../../lib/repair-kb-client';
import type { KbTaxonomy, KbManufacturer, KbDeviceFamily, KbModel, KbBoardRevision } from '../../lib/repair-kb-client';
import { ArrowLeft, Plus, Trash2, Loader, Check, X, Edit3 } from '../../lib/icons';

// ── Styles ────────────────────────────────────────────────────────────────────

const inp = 'w-full bg-black/40 border border-gray-700 rounded-xl px-3 py-2 text-[#E0E0E0] placeholder-gray-600 focus:outline-none focus:border-[#FF1744]/60 text-sm';
const sel = inp + ' cursor-pointer';
const btn = (v: 'primary' | 'secondary' | 'danger') => {
  const base = 'flex items-center gap-2 px-3 py-1.5 rounded-lg font-semibold text-xs transition-colors disabled:opacity-50';
  if (v === 'primary')   return base + ' bg-[#FF1744] hover:bg-[#D50000] text-white';
  if (v === 'secondary') return base + ' bg-gray-800 hover:bg-gray-700 border border-gray-700 text-[#E0E0E0]';
  return base + ' bg-red-900/40 hover:bg-red-800/60 border border-red-800 text-red-300';
};

type TabId = 'manufacturers' | 'device-families' | 'models' | 'board-revisions';

// ── Inline row editor ─────────────────────────────────────────────────────────

interface EditRowProps {
  fields: { key: string; label: string; placeholder?: string; type?: string }[];
  initial: Record<string, string | number>;
  onSave: (data: Record<string, unknown>) => Promise<void>;
  onCancel: () => void;
}

function EditRow({ fields, initial, onSave, onCancel }: EditRowProps) {
  const [vals, setVals] = useState<Record<string, string>>(
    Object.fromEntries(fields.map(f => [f.key, String(initial[f.key] ?? '')]))
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setErr('');
    try {
      const data: Record<string, unknown> = {};
      for (const f of fields) {
        data[f.key] = f.type === 'number' ? parseInt(vals[f.key]) || 0 : vals[f.key];
      }
      await onSave(data);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 space-y-3">
      <div className={`grid gap-3`} style={{ gridTemplateColumns: `repeat(${fields.length}, 1fr)` }}>
        {fields.map(f => (
          <div key={f.key}>
            <label className="text-xs text-gray-500 mb-1 block">{f.label}</label>
            <input
              className={inp}
              type={f.type ?? 'text'}
              placeholder={f.placeholder ?? f.label}
              value={vals[f.key]}
              onChange={e => setVals(v => ({ ...v, [f.key]: e.target.value }))}
            />
          </div>
        ))}
      </div>
      {err && <p className="text-xs text-red-400">{err}</p>}
      <div className="flex gap-2">
        <button onClick={handleSave} disabled={saving} className={btn('primary')}>
          {saving ? <Loader className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
          Save
        </button>
        <button onClick={onCancel} className={btn('secondary')}><X className="h-3 w-3" /> Cancel</button>
      </div>
    </div>
  );
}

// ── Manufacturers tab ─────────────────────────────────────────────────────────

function ManufacturersTab({ taxonomy, onReload }: { taxonomy: KbTaxonomy; onReload: () => void }) {
  const [adding, setAdding]   = useState(false);
  const [editId, setEditId]   = useState<string | null>(null);

  const handleCreate = async (data: Record<string, unknown>) => {
    await createTaxonomyItem('manufacturers', data);
    setAdding(false);
    onReload();
  };

  const handleUpdate = async (id: string, data: Record<string, unknown>) => {
    await updateTaxonomyItem('manufacturers', id, data);
    setEditId(null);
    onReload();
  };

  const handleDelete = async (m: KbManufacturer) => {
    if (!confirm(`Delete "${m.name}"? This will fail if it has device families.`)) return;
    try {
      await deleteTaxonomyItem('manufacturers', m.id);
      onReload();
    } catch (e) {
      alert('Delete failed: ' + (e as Error).message);
    }
  };

  const FIELDS = [
    { key: 'name', label: 'Name', placeholder: 'Sony' },
    { key: 'slug', label: 'Slug', placeholder: 'sony' },
    { key: 'sort_order', label: 'Sort', placeholder: '10', type: 'number' },
  ];

  return (
    <div className="space-y-3">
      {taxonomy.manufacturers.map(m => (
        <div key={m.id}>
          {editId === m.id ? (
            <EditRow
              fields={FIELDS}
              initial={{ name: m.name, slug: m.slug, sort_order: m.sort_order }}
              onSave={data => handleUpdate(m.id, data)}
              onCancel={() => setEditId(null)}
            />
          ) : (
            <div className="flex items-center gap-3 bg-gray-900 border border-gray-800 rounded-xl px-4 py-3">
              <span className="text-xs text-gray-600 font-mono w-6 text-right">{m.sort_order}</span>
              <span className="font-medium text-[#E0E0E0] flex-1">{m.name}</span>
              <span className="text-xs text-gray-500 font-mono">{m.slug}</span>
              <button onClick={() => setEditId(m.id)} className={btn('secondary')}><Edit3 className="h-3 w-3" /></button>
              <button onClick={() => handleDelete(m)} className="text-gray-600 hover:text-red-400 transition-colors"><Trash2 className="h-4 w-4" /></button>
            </div>
          )}
        </div>
      ))}

      {adding ? (
        <EditRow
          fields={FIELDS}
          initial={{ name: '', slug: '', sort_order: (taxonomy.manufacturers.length + 1) * 10 }}
          onSave={handleCreate}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <button onClick={() => setAdding(true)} className={btn('secondary') + ' w-full justify-center py-3'}>
          <Plus className="h-4 w-4" /> Add Manufacturer
        </button>
      )}
    </div>
  );
}

// ── Device Families tab ───────────────────────────────────────────────────────

function DeviceFamiliesTab({ taxonomy, onReload }: { taxonomy: KbTaxonomy; onReload: () => void }) {
  const [adding, setAdding]       = useState(false);
  const [editId, setEditId]       = useState<string | null>(null);
  const [filterMfr, setFilterMfr] = useState('');

  const families = filterMfr
    ? taxonomy.device_families.filter(f => f.manufacturer_id === filterMfr)
    : taxonomy.device_families;

  const handleCreate = async (data: Record<string, unknown>) => {
    await createTaxonomyItem('device-families', data);
    setAdding(false);
    onReload();
  };

  const handleUpdate = async (id: string, data: Record<string, unknown>) => {
    await updateTaxonomyItem('device-families', id, data);
    setEditId(null);
    onReload();
  };

  const handleDelete = async (f: KbDeviceFamily) => {
    if (!confirm(`Delete "${f.name}"? This will fail if it has board revisions or models.`)) return;
    try {
      await deleteTaxonomyItem('device-families', f.id);
      onReload();
    } catch (e) {
      alert('Delete failed: ' + (e as Error).message);
    }
  };

  const FIELDS = (mfrId?: string) => [
    { key: 'manufacturer_id', label: 'Manufacturer ID', placeholder: mfrId ?? 'UUID' },
    { key: 'name', label: 'Name', placeholder: 'PlayStation 5' },
    { key: 'slug', label: 'Slug', placeholder: 'playstation-5' },
    { key: 'sort_order', label: 'Sort', placeholder: '10', type: 'number' },
  ];

  return (
    <div className="space-y-3">
      <select className={sel + ' max-w-xs'} value={filterMfr} onChange={e => setFilterMfr(e.target.value)}>
        <option value="">All manufacturers</option>
        {taxonomy.manufacturers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
      </select>

      {families.map(f => {
        const mfr = taxonomy.manufacturers.find(m => m.id === f.manufacturer_id);
        return (
          <div key={f.id}>
            {editId === f.id ? (
              <EditRow
                fields={FIELDS(f.manufacturer_id)}
                initial={{ manufacturer_id: f.manufacturer_id, name: f.name, slug: f.slug, sort_order: f.sort_order }}
                onSave={data => handleUpdate(f.id, data)}
                onCancel={() => setEditId(null)}
              />
            ) : (
              <div className="flex items-center gap-3 bg-gray-900 border border-gray-800 rounded-xl px-4 py-3">
                <span className="text-xs text-gray-600 font-mono w-6 text-right">{f.sort_order}</span>
                <span className="font-medium text-[#E0E0E0] flex-1">{f.name}</span>
                <span className="text-xs text-gray-500">{mfr?.name ?? f.manufacturer_id.slice(0,8)}</span>
                <span className="text-xs text-gray-600 font-mono">{f.slug}</span>
                <button onClick={() => setEditId(f.id)} className={btn('secondary')}><Edit3 className="h-3 w-3" /></button>
                <button onClick={() => handleDelete(f)} className="text-gray-600 hover:text-red-400 transition-colors"><Trash2 className="h-4 w-4" /></button>
              </div>
            )}
          </div>
        );
      })}

      {adding ? (
        <EditRow
          fields={FIELDS(filterMfr || undefined)}
          initial={{ manufacturer_id: filterMfr, name: '', slug: '', sort_order: (families.length + 1) * 10 }}
          onSave={handleCreate}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <button onClick={() => setAdding(true)} className={btn('secondary') + ' w-full justify-center py-3'}>
          <Plus className="h-4 w-4" /> Add Device Family
        </button>
      )}
    </div>
  );
}

// ── Models tab ────────────────────────────────────────────────────────────────

function ModelsTab({ taxonomy, onReload }: { taxonomy: KbTaxonomy; onReload: () => void }) {
  const [adding, setAdding]           = useState(false);
  const [editId, setEditId]           = useState<string | null>(null);
  const [filterFamily, setFilterFamily] = useState('');

  const models = filterFamily
    ? taxonomy.models.filter(m => m.device_family_id === filterFamily)
    : taxonomy.models;

  const handleCreate = async (data: Record<string, unknown>) => {
    await createTaxonomyItem('models', data);
    setAdding(false);
    onReload();
  };

  const handleUpdate = async (id: string, data: Record<string, unknown>) => {
    await updateTaxonomyItem('models', id, data);
    setEditId(null);
    onReload();
  };

  const handleDelete = async (m: KbModel) => {
    if (!confirm(`Delete "${m.name}"?`)) return;
    try {
      await deleteTaxonomyItem('models', m.id);
      onReload();
    } catch (e) {
      alert('Delete failed: ' + (e as Error).message);
    }
  };

  const FIELDS = (familyId?: string) => [
    { key: 'device_family_id', label: 'Family ID', placeholder: familyId ?? 'UUID' },
    { key: 'name', label: 'Name', placeholder: 'PS5 Slim' },
    { key: 'slug', label: 'Slug', placeholder: 'ps5-slim' },
    { key: 'sort_order', label: 'Sort', placeholder: '10', type: 'number' },
  ];

  return (
    <div className="space-y-3">
      <select className={sel + ' max-w-xs'} value={filterFamily} onChange={e => setFilterFamily(e.target.value)}>
        <option value="">All families</option>
        {taxonomy.device_families.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
      </select>

      {models.map(m => {
        const family = taxonomy.device_families.find(f => f.id === m.device_family_id);
        return (
          <div key={m.id}>
            {editId === m.id ? (
              <EditRow
                fields={FIELDS(m.device_family_id)}
                initial={{ device_family_id: m.device_family_id, name: m.name, slug: m.slug, sort_order: m.sort_order }}
                onSave={data => handleUpdate(m.id, data)}
                onCancel={() => setEditId(null)}
              />
            ) : (
              <div className="flex items-center gap-3 bg-gray-900 border border-gray-800 rounded-xl px-4 py-3">
                <span className="text-xs text-gray-600 font-mono w-6 text-right">{m.sort_order}</span>
                <span className="font-medium text-[#E0E0E0] flex-1">{m.name}</span>
                <span className="text-xs text-gray-500">{family?.name ?? m.device_family_id.slice(0,8)}</span>
                <span className="text-xs text-gray-600 font-mono">{m.slug}</span>
                <button onClick={() => setEditId(m.id)} className={btn('secondary')}><Edit3 className="h-3 w-3" /></button>
                <button onClick={() => handleDelete(m)} className="text-gray-600 hover:text-red-400 transition-colors"><Trash2 className="h-4 w-4" /></button>
              </div>
            )}
          </div>
        );
      })}

      {adding ? (
        <EditRow
          fields={FIELDS(filterFamily || undefined)}
          initial={{ device_family_id: filterFamily, name: '', slug: '', sort_order: (models.length + 1) * 10 }}
          onSave={handleCreate}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <button onClick={() => setAdding(true)} className={btn('secondary') + ' w-full justify-center py-3'}>
          <Plus className="h-4 w-4" /> Add Model
        </button>
      )}
    </div>
  );
}

// ── Board Revisions tab ───────────────────────────────────────────────────────

function BoardRevisionsTab({ taxonomy, onReload }: { taxonomy: KbTaxonomy; onReload: () => void }) {
  const [adding, setAdding]           = useState(false);
  const [editId, setEditId]           = useState<string | null>(null);
  const [filterFamily, setFilterFamily] = useState('');

  const revisions = filterFamily
    ? taxonomy.board_revisions.filter(b => b.device_family_id === filterFamily)
    : taxonomy.board_revisions;

  const handleCreate = async (data: Record<string, unknown>) => {
    await createTaxonomyItem('board-revisions', data);
    setAdding(false);
    onReload();
  };

  const handleUpdate = async (id: string, data: Record<string, unknown>) => {
    await updateTaxonomyItem('board-revisions', id, data);
    setEditId(null);
    onReload();
  };

  const handleDelete = async (b: KbBoardRevision) => {
    if (!confirm(`Delete "${b.name}"? This will fail if references use it.`)) return;
    try {
      await deleteTaxonomyItem('board-revisions', b.id);
      onReload();
    } catch (e) {
      alert('Delete failed: ' + (e as Error).message);
    }
  };

  const FIELDS = (familyId?: string) => [
    { key: 'device_family_id', label: 'Family ID', placeholder: familyId ?? 'UUID' },
    { key: 'name', label: 'Name', placeholder: 'EDM-030' },
    { key: 'slug', label: 'Slug', placeholder: 'edm-030' },
    { key: 'sort_order', label: 'Sort', placeholder: '10', type: 'number' },
  ];

  return (
    <div className="space-y-3">
      <select className={sel + ' max-w-xs'} value={filterFamily} onChange={e => setFilterFamily(e.target.value)}>
        <option value="">All families</option>
        {taxonomy.device_families.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
      </select>

      {revisions.map(b => {
        const family = taxonomy.device_families.find(f => f.id === b.device_family_id);
        return (
          <div key={b.id}>
            {editId === b.id ? (
              <EditRow
                fields={FIELDS(b.device_family_id)}
                initial={{ device_family_id: b.device_family_id, name: b.name, slug: b.slug, sort_order: b.sort_order }}
                onSave={data => handleUpdate(b.id, data)}
                onCancel={() => setEditId(null)}
              />
            ) : (
              <div className="flex items-center gap-3 bg-gray-900 border border-gray-800 rounded-xl px-4 py-3">
                <span className="text-xs text-gray-600 font-mono w-6 text-right">{b.sort_order}</span>
                <span className="font-medium text-[#E0E0E0] font-mono flex-1">{b.name}</span>
                <span className="text-xs text-gray-500">{family?.name ?? b.device_family_id.slice(0,8)}</span>
                <span className="text-xs text-gray-600 font-mono">{b.slug}</span>
                <button onClick={() => setEditId(b.id)} className={btn('secondary')}><Edit3 className="h-3 w-3" /></button>
                <button onClick={() => handleDelete(b)} className="text-gray-600 hover:text-red-400 transition-colors"><Trash2 className="h-4 w-4" /></button>
              </div>
            )}
          </div>
        );
      })}

      {adding ? (
        <EditRow
          fields={FIELDS(filterFamily || undefined)}
          initial={{ device_family_id: filterFamily, name: '', slug: '', sort_order: (revisions.length + 1) * 10 }}
          onSave={handleCreate}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <button onClick={() => setAdding(true)} className={btn('secondary') + ' w-full justify-center py-3'}>
          <Plus className="h-4 w-4" /> Add Board Revision
        </button>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const TAB_LABELS: { id: TabId; label: string }[] = [
  { id: 'manufacturers',   label: 'Manufacturers' },
  { id: 'device-families', label: 'Device Families' },
  { id: 'models',          label: 'Models' },
  { id: 'board-revisions', label: 'Board Revisions' },
];

function TaxonomyContent({ onExpired }: { onExpired: () => void }) {
  const [tab, setTab]           = useState<TabId>('manufacturers');
  const [taxonomy, setTaxonomy] = useState<KbTaxonomy>({ manufacturers: [], device_families: [], models: [], board_revisions: [] });
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);

  const load = useCallback(() => {
    invalidateTaxonomyCache();
    setLoading(true);
    setError(null);
    fetchTaxonomy()
      .then(t  => { setTaxonomy(t); setLoading(false); })
      .catch(e => {
        const msg = (e as Error).message;
        if (msg.toLowerCase().includes('unauthorized')) { onExpired(); return; }
        setError(msg);
        setLoading(false);
      });
  }, [onExpired]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-[#E0E0E0]">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-[#0D0D0D]/95 border-b border-gray-800 backdrop-blur-sm">
        {/* Breadcrumb */}
        <div className="max-w-4xl mx-auto px-6 pt-2.5 flex items-center gap-1.5 text-xs text-gray-600">
          <Link to="/admin/edit-site" className="hover:text-gray-400 transition-colors">Admin</Link>
          <span>›</span>
          <Link to="/admin/repair-knowledge-base" className="hover:text-gray-400 transition-colors">Repair Knowledge Base</Link>
          <span>›</span>
          <span className="text-gray-400">Manage Taxonomy</span>
        </div>
        {/* Title row */}
        <div className="max-w-4xl mx-auto px-6 py-2 flex items-center gap-3">
          <Link to="/admin/repair-knowledge-base"
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#E0E0E0] transition-colors flex-shrink-0">
            <ArrowLeft className="h-4 w-4" /> Back to Repair Knowledge Base
          </Link>
          <h1 className="text-base font-bold text-[#E0E0E0] flex-1 min-w-0">Manage Taxonomy</h1>
        </div>
        {/* Tabs */}
        <div className="max-w-4xl mx-auto px-6 flex gap-1 pb-0">
          {TAB_LABELS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? 'border-[#FF1744] text-[#FF1744]'
                  : 'border-transparent text-gray-500 hover:text-[#E0E0E0]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {loading && <div className="flex justify-center py-12"><Loader className="h-6 w-6 animate-spin text-[#FF1744]" /></div>}
        {error && <p className="text-red-400 text-sm mb-4">{error}</p>}

        {!loading && (
          <>
            {tab === 'manufacturers'   && <ManufacturersTab   taxonomy={taxonomy} onReload={load} />}
            {tab === 'device-families' && <DeviceFamiliesTab  taxonomy={taxonomy} onReload={load} />}
            {tab === 'models'          && <ModelsTab          taxonomy={taxonomy} onReload={load} />}
            {tab === 'board-revisions' && <BoardRevisionsTab  taxonomy={taxonomy} onReload={load} />}
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminTaxonomyPage() {
  const [authed, setAuthed] = useState(isAdminAuthed());
  const handleExpired = useCallback(() => {
    clearAdminAuth();
    setAuthed(false);
  }, []);
  return authed
    ? <TaxonomyContent onExpired={handleExpired} />
    : <AdminLoginGate onAuth={() => setAuthed(true)} />;
}
